import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/outline";

type Agent = { id: number; name: string; email: string };
type Tally = { total: number; connected: number; notConnected: number };
type UserCount = { id: number; name: string } & Tally;

type DayCall = {
  id: number;
  startedAt: number | null;
  durationSec: number;
  connected: boolean;
  status: string | null;
  missedReason: string | null;
  number: string | null;
  contact: string | null;
  rep: string;
  recording: string | null;
};
type DayStats = {
  total: number;
  connected: number;
  notConnected: number;
  talkTimeSec: number;
  avgConnectedSec: number;
  longestSec: number;
  hourly: number[];
};
type Utterance = { speaker: "internal" | "external"; text: string; start: number };
type TranscriptState = {
  status: "loading" | "ready" | "unavailable" | "error";
  utterances?: Utterance[];
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Seconds -> "1h 4m" / "3m 20s" / "45s".
function formatDuration(sec: number): string {
  if (!sec) return "0s";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

// --- date helpers (operate in the browser's local time) ---
const pad = (n: number) => String(n).padStart(2, "0");
const toDateStr = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const firstOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const lastOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

// Day count -> threshold color. >=30 green, 15–29 yellow, <15 red.
function countClasses(n: number): string {
  if (n >= 30) return "bg-green-100 text-green-800 border-green-300 dark:bg-green-950/40 dark:text-green-400 dark:border-green-900";
  if (n >= 15) return "bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-950/40 dark:text-amber-400 dark:border-amber-900";
  return "bg-red-100 text-red-700 border-red-300 dark:bg-red-950/40 dark:text-red-400 dark:border-red-900";
}

function monthsBetween(startStr: string, endStr: string): { year: number; month: number }[] {
  const [sy, sm] = startStr.split("-").map(Number);
  const [ey, em] = endStr.split("-").map(Number);
  const months: { year: number; month: number }[] = [];
  let y = sy;
  let m = sm;
  while (y < ey || (y === ey && m <= em)) {
    months.push({ year: y, month: m });
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return months;
}

const Spinner = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
  </svg>
);

/**
 * Aircall outbound call activity (calendar + day-detail drawer). Auth-free:
 * the parent portal supplies the verified `adminEmail` (sent as x-admin-email)
 * and `onUnauthorized` to call when the server rejects the email (403).
 */
function SalesCallActivity({
  adminEmail,
  onUnauthorized,
}: {
  adminEmail: string;
  onUnauthorized: () => void;
}) {
  // --- data / filters ---
  const now = new Date();
  const [startDate, setStartDate] = useState(toDateStr(firstOfMonth(now)));
  const [endDate, setEndDate] = useState(toDateStr(lastOfMonth(now)));
  const [callerId, setCallerId] = useState<string>(""); // "" = whole team
  const [agents, setAgents] = useState<Agent[]>([]);
  const [dailyCounts, setDailyCounts] = useState<Record<string, Tally>>({});
  const [total, setTotal] = useState(0);
  const [connected, setConnected] = useState(0);
  const [notConnected, setNotConnected] = useState(0);
  const [byUser, setByUser] = useState<UserCount[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState("");

  // --- day-detail drawer ---
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false); // animation target (slide/fade)
  const [dayCalls, setDayCalls] = useState<DayCall[]>([]);
  const [dayStats, setDayStats] = useState<DayStats | null>(null);
  const [dayLoading, setDayLoading] = useState(false);
  const [dayError, setDayError] = useState("");
  const [daySort, setDaySort] = useState<"time" | "duration">("time");
  const [expandedCallId, setExpandedCallId] = useState<number | null>(null);
  const [transcripts, setTranscripts] = useState<Record<number, TranscriptState>>({});

  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC", []);
  const todayStr = useMemo(() => toDateStr(new Date()), []);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await axios.get("/api/sales/agents", { headers: { "x-admin-email": adminEmail } });
      setAgents(res.data.agents || []);
    } catch (err) {
      // Non-fatal: the team-total calendar still works without the dropdown.
      if (axios.isAxiosError(err) && err.response?.status === 403) onUnauthorized();
    }
  }, [adminEmail, onUnauthorized]);

  const fetchCalls = useCallback(async () => {
    if (!adminEmail) return;
    setDataLoading(true);
    setDataError("");
    try {
      const res = await axios.get("/api/sales/calls", {
        headers: { "x-admin-email": adminEmail },
        params: { start: startDate, end: endDate, tz, userId: callerId || undefined },
      });
      setDailyCounts(res.data.dailyCounts || {});
      setTotal(res.data.total || 0);
      setConnected(res.data.connected || 0);
      setNotConnected(res.data.notConnected || 0);
      setByUser(res.data.byUser || []);
    } catch (err) {
      setDailyCounts({});
      setTotal(0);
      setConnected(0);
      setNotConnected(0);
      setByUser([]);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 403) {
          onUnauthorized();
          return;
        }
        if (err.response?.status === 503) {
          setDataError("Aircall is not configured on the server (set AIRCALL_API_ID / AIRCALL_API_TOKEN).");
          return;
        }
      }
      setDataError("Failed to load call data. Please try again.");
    } finally {
      setDataLoading(false);
    }
  }, [adminEmail, startDate, endDate, callerId, tz, onUnauthorized]);

  const openDay = useCallback(
    async (date: string) => {
      setSelectedDay(date);
      setDayLoading(true);
      setDayError("");
      setDayCalls([]);
      setDayStats(null);
      setExpandedCallId(null);
      setTranscripts({});
      try {
        const res = await axios.get("/api/sales/calls/day", {
          headers: { "x-admin-email": adminEmail },
          params: { date, tz, userId: callerId || undefined },
        });
        setDayCalls(res.data.calls || []);
        setDayStats(res.data.stats || null);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 403) {
          onUnauthorized();
          return;
        }
        setDayError("Failed to load calls for this day.");
      } finally {
        setDayLoading(false);
      }
    },
    [adminEmail, tz, callerId, onUnauthorized],
  );

  const fetchTranscript = useCallback(
    async (callId: number) => {
      setTranscripts((prev) => ({ ...prev, [callId]: { status: "loading" } }));
      try {
        const res = await axios.get(`/api/sales/calls/${callId}/transcription`, {
          headers: { "x-admin-email": adminEmail },
        });
        if (res.data.available) {
          setTranscripts((prev) => ({
            ...prev,
            [callId]: { status: "ready", utterances: res.data.utterances || [] },
          }));
        } else {
          setTranscripts((prev) => ({ ...prev, [callId]: { status: "unavailable" } }));
        }
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 403) {
          onUnauthorized();
          return;
        }
        setTranscripts((prev) => ({ ...prev, [callId]: { status: "error" } }));
      }
    },
    [adminEmail, onUnauthorized],
  );

  // Plain fn (not memoized) so it reads fresh expandedCallId/transcripts.
  const toggleCall = (callId: number) => {
    if (expandedCallId === callId) {
      setExpandedCallId(null);
      return;
    }
    setExpandedCallId(callId);
    if (!transcripts[callId]) fetchTranscript(callId);
  };

  const formatTime = useCallback(
    (unix: number | null) =>
      unix
        ? new Date(unix * 1000).toLocaleTimeString("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" })
        : "—",
    [tz],
  );

  const sortedDayCalls = useMemo(() => {
    const arr = [...dayCalls];
    if (daySort === "duration") arr.sort((a, b) => b.durationSec - a.durationSec);
    else arr.sort((a, b) => (a.startedAt || 0) - (b.startedAt || 0));
    return arr;
  }, [dayCalls, daySort]);

  const dayHeading = useMemo(
    () =>
      selectedDay
        ? new Date(`${selectedDay}T00:00:00`).toLocaleDateString("en-US", {
            weekday: "long",
            month: "long",
            day: "numeric",
            year: "numeric",
          })
        : "",
    [selectedDay],
  );

  // Animated close: slide out, then unmount after the transition.
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    window.setTimeout(() => setSelectedDay(null), 300);
  }, []);

  // Drive the slide-in once mounted; reset the flag when fully closed.
  useEffect(() => {
    if (selectedDay) {
      const id = requestAnimationFrame(() => setDrawerOpen(true));
      return () => cancelAnimationFrame(id);
    }
    setDrawerOpen(false);
  }, [selectedDay]);

  // Drawer data goes stale when filters change — close it immediately.
  useEffect(() => {
    setSelectedDay(null);
  }, [callerId, startDate, endDate]);

  // Esc closes the drawer.
  useEffect(() => {
    if (!selectedDay) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeDrawer();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedDay, closeDrawer]);

  // Load agents once; refetch calls when filters change.
  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  // --- date-range controls ---
  const shiftMonth = (delta: number) => {
    const [y, m] = startDate.split("-").map(Number);
    const base = new Date(y, m - 1 + delta, 1);
    setStartDate(toDateStr(firstOfMonth(base)));
    setEndDate(toDateStr(lastOfMonth(base)));
  };

  const applyQuickRange = (range: "thisMonth" | "lastMonth" | "last7" | "last30") => {
    const today = new Date();
    if (range === "thisMonth") {
      setStartDate(toDateStr(firstOfMonth(today)));
      setEndDate(toDateStr(lastOfMonth(today)));
    } else if (range === "lastMonth") {
      const base = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      setStartDate(toDateStr(firstOfMonth(base)));
      setEndDate(toDateStr(lastOfMonth(base)));
    } else if (range === "last7") {
      setStartDate(toDateStr(addDays(today, -6)));
      setEndDate(toDateStr(today));
    } else {
      setStartDate(toDateStr(addDays(today, -29)));
      setEndDate(toDateStr(today));
    }
  };

  const rangeLabel = useMemo(() => {
    const fmt = (s: string) =>
      new Date(`${s}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return `${fmt(startDate)} – ${fmt(endDate)}`;
  }, [startDate, endDate]);

  const callerLabel = callerId
    ? agents.find((a) => String(a.id) === callerId)?.name || "Selected caller"
    : "Whole team";

  return (
    <>
      <div className="space-y-6">
        {/* Filters */}
        <div className="bg-base-100 rounded-xl shadow-md dark:border dark:border-base-300 p-6">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <p className="font-semibold pb-1 text-sm text-gray-700 dark:text-base-content/80">Caller</p>
              <select
                value={callerId}
                onChange={(e) => setCallerId(e.target.value)}
                className="select select-bordered min-w-[14rem]"
              >
                <option value="">All callers (team total)</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <p className="font-semibold pb-1 text-sm text-gray-700 dark:text-base-content/80">From</p>
              <input
                type="date"
                value={startDate}
                max={endDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input input-bordered"
              />
            </div>
            <div>
              <p className="font-semibold pb-1 text-sm text-gray-700 dark:text-base-content/80">To</p>
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="input input-bordered"
              />
            </div>

            <div className="flex gap-1">
              {([
                ["thisMonth", "This month"],
                ["lastMonth", "Last month"],
                ["last7", "Last 7 days"],
                ["last30", "Last 30 days"],
              ] as const).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => applyQuickRange(key)}
                  className="px-3 py-1.5 text-xs font-medium rounded-full text-gray-600 dark:text-base-content/70 bg-gray-100 dark:bg-base-200 hover:bg-base-300 transition-colors"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-base-100 rounded-xl shadow-md dark:border dark:border-base-300 p-6">
            <p className="text-sm text-base-content/60">Total outbound calls</p>
            <div className="h-10 mt-1 flex items-center">
              {dataLoading ? (
                <Spinner className="h-7 w-7 text-gray-300 dark:text-base-content/30" />
              ) : (
                <p className="text-4xl font-bold text-base-content">{total}</p>
              )}
            </div>
            {!dataLoading && (
              <p className="text-xs text-base-content/60 mt-1 flex items-center gap-2">
                <span className="flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                  {connected} connected
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded-full bg-rose-400" />
                  {notConnected} not
                </span>
              </p>
            )}
            <p className="text-xs text-base-content/40 mt-2">
              {callerLabel} · {rangeLabel}
            </p>
          </div>
          <div className="bg-base-100 rounded-xl shadow-md dark:border dark:border-base-300 p-6 sm:col-span-2">
            <p className="text-sm text-base-content/60 mb-2">Calls by caller (this range)</p>
            {byUser.length === 0 ? (
              <p className="text-sm text-base-content/40">{dataLoading ? "Loading…" : "No calls in range."}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {byUser.slice(0, 12).map((u) => (
                  <span
                    key={u.id}
                    title={`Connected: ${u.connected} · Not connected: ${u.notConnected}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-100 dark:bg-base-200 text-sm text-gray-700 dark:text-base-content/80"
                  >
                    {u.name}
                    <span className="font-semibold text-base-content">{u.total}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {dataError && (
          <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-400 rounded-xl p-4 text-sm">{dataError}</div>
        )}

        {/* Calendar */}
        <div className="bg-base-100 rounded-xl shadow-md dark:border dark:border-base-300 p-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => shiftMonth(-1)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-base-200 text-gray-600 dark:text-base-content/70"
              aria-label="Previous month"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-4 text-xs text-base-content/60">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded bg-green-300" /> &ge; 30
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded bg-yellow-300" /> 15–29
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded bg-red-300" /> &lt; 15
              </span>
            </div>
            <button
              onClick={() => shiftMonth(1)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-base-200 text-gray-600 dark:text-base-content/70"
              aria-label="Next month"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>

          {dataLoading && (
            <div className="flex items-center justify-center gap-2 text-sm text-base-content/60 py-2 mb-2">
              <Spinner className="h-4 w-4" />
              Loading call data…
            </div>
          )}

          <div className="space-y-8">
            {monthsBetween(startDate, endDate).map(({ year, month }) => {
              const daysInMonth = new Date(year, month, 0).getDate();
              const firstWeekday = new Date(year, month - 1, 1).getDay();
              const monthName = new Date(year, month - 1, 1).toLocaleDateString("en-US", {
                month: "long",
                year: "numeric",
              });
              const cells: (number | null)[] = [
                ...Array(firstWeekday).fill(null),
                ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
              ];

              return (
                <div key={`${year}-${month}`}>
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-base-content/80 mb-3">{monthName}</h2>
                  <div className="grid grid-cols-7 gap-1.5">
                    {WEEKDAYS.map((d) => (
                      <div key={d} className="text-center text-xs font-medium text-base-content/40 pb-1">
                        {d}
                      </div>
                    ))}
                    {cells.map((day, idx) => {
                      if (day === null) return <div key={`b-${idx}`} />;
                      const key = `${year}-${pad(month)}-${pad(day)}`;
                      const inRange = key >= startDate && key <= endDate;
                      const isFuture = key > todayStr;
                      const stat = dailyCounts[key];
                      const count = stat?.total || 0;
                      const connectedPct = count > 0 ? ((stat?.connected || 0) / count) * 100 : 0;

                      let cls = "bg-base-200 text-gray-300 dark:text-base-content/30 border-base-200";
                      let showCount = false;
                      if (inRange && !isFuture) {
                        if (dataLoading) {
                          // Skeleton while fetching — avoids flashing red zeros.
                          cls = "bg-gray-100 dark:bg-base-200 border-base-200 animate-pulse";
                        } else {
                          cls = countClasses(count);
                          showCount = true;
                        }
                      } else if (inRange && isFuture) {
                        cls = "bg-base-100 text-gray-300 dark:text-base-content/30 border-base-200";
                      }

                      const clickable = inRange && !isFuture && !dataLoading;

                      return (
                        <div
                          key={key}
                          onClick={clickable ? () => openDay(key) : undefined}
                          className={`group relative aspect-square rounded-lg border flex flex-col items-center justify-center ${cls} ${
                            clickable ? "cursor-pointer hover:ring-2 hover:ring-blue-400/60" : ""
                          }`}
                        >
                          <span className="absolute top-1 left-1.5 text-[10px] font-medium opacity-60">{day}</span>
                          {showCount && (
                            <>
                              <span className="text-xl font-bold leading-none">{count}</span>

                              {/* Connected (green) vs not-connected (red) split.
                                  Sits on a translucent track so it reads on any
                                  cell color. Hover the cell for exact numbers. */}
                              {count > 0 && (
                                <div className="absolute bottom-1.5 left-1.5 right-1.5 h-1.5 flex rounded-full overflow-hidden bg-white/50">
                                  <div className="h-full bg-emerald-500" style={{ width: `${connectedPct}%` }} />
                                  <div className="h-full bg-rose-400" style={{ width: `${100 - connectedPct}%` }} />
                                </div>
                              )}

                              {/* Hover tooltip */}
                              <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-20 whitespace-nowrap rounded-md bg-gray-900 text-white text-[11px] px-2 py-1.5 shadow-lg">
                                <div className="flex items-center gap-1.5">
                                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-400" />
                                  Connected: <span className="font-semibold">{stat?.connected || 0}</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <span className="inline-block w-2 h-2 rounded-full bg-rose-400" />
                                  Not connected: <span className="font-semibold">{stat?.notConnected || 0}</span>
                                </div>
                                <div className="mt-0.5 pt-0.5 border-t border-white/20">
                                  Total: <span className="font-semibold">{count}</span>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Day-detail drawer */}
      {selectedDay && (
        <div className="fixed inset-0 z-40">
          <div
            onClick={closeDrawer}
            className={`absolute inset-0 bg-black/30 transition-opacity duration-300 ${
              drawerOpen ? "opacity-100" : "opacity-0"
            }`}
          />
          <div
            className={`absolute right-0 top-0 h-full w-full max-w-md bg-base-100 shadow-2xl overflow-y-auto transition-transform duration-300 ease-out ${
              drawerOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="sticky top-0 bg-base-100 border-b border-base-300 px-6 py-4 flex items-start justify-between z-10">
              <div>
                <h2 className="text-lg font-bold text-base-content">{dayHeading}</h2>
                <p className="text-sm text-base-content/60">{callerLabel}</p>
              </div>
              <button
                onClick={closeDrawer}
                className="p-2 -mr-2 rounded-lg hover:bg-gray-100 dark:hover:bg-base-200 text-base-content/60"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-6">
              {dayLoading ? (
                <div className="flex items-center justify-center gap-2 text-sm text-base-content/60 py-12">
                  <Spinner className="h-5 w-5" /> Loading calls…
                </div>
              ) : dayError ? (
                <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-400 rounded-lg p-3 text-sm">{dayError}</div>
              ) : dayStats && dayStats.total === 0 ? (
                <p className="text-sm text-base-content/40 py-12 text-center">No outbound calls on this day.</p>
              ) : dayStats ? (
                <>
                  {/* Connect-rate donut + talk-time stats */}
                  <div className="flex items-center gap-5">
                    {(() => {
                      const connPct = dayStats.total > 0 ? (dayStats.connected / dayStats.total) * 100 : 0;
                      return (
                        <div
                          className="relative w-20 h-20 rounded-full shrink-0"
                          style={{ background: `conic-gradient(#10b981 ${connPct}%, #fb7185 ${connPct}% 100%)` }}
                        >
                          <div className="absolute inset-[7px] rounded-full bg-base-100 flex flex-col items-center justify-center">
                            <span className="text-base font-bold text-base-content leading-none">{Math.round(connPct)}%</span>
                            <span className="text-[9px] text-base-content/40">connected</span>
                          </div>
                        </div>
                      );
                    })()}
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                      <div>
                        <p className="text-base-content/40 text-xs">Connected</p>
                        <p className="font-semibold text-base-content">
                          {dayStats.connected} / {dayStats.total}
                        </p>
                      </div>
                      <div>
                        <p className="text-base-content/40 text-xs">Talk time</p>
                        <p className="font-semibold text-base-content">{formatDuration(dayStats.talkTimeSec)}</p>
                      </div>
                      <div>
                        <p className="text-base-content/40 text-xs">Avg (connected)</p>
                        <p className="font-semibold text-base-content">{formatDuration(dayStats.avgConnectedSec)}</p>
                      </div>
                      <div>
                        <p className="text-base-content/40 text-xs">Longest</p>
                        <p className="font-semibold text-base-content">{formatDuration(dayStats.longestSec)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Hourly distribution */}
                  <div>
                    <p className="text-xs font-medium text-base-content/60 mb-2">Calls by hour</p>
                    {(() => {
                      const maxHour = Math.max(1, ...dayStats.hourly);
                      return (
                        <>
                          <div className="flex items-end gap-0.5 h-16">
                            {dayStats.hourly.map((n, h) => (
                              <div
                                key={h}
                                title={`${((h + 11) % 12) + 1}${h < 12 ? "a" : "p"} — ${n} call${n !== 1 ? "s" : ""}`}
                                className="flex-1 h-full bg-blue-100 dark:bg-blue-900/60 rounded-sm flex items-end"
                              >
                                <div
                                  className="w-full bg-blue-500 rounded-sm"
                                  style={{ height: `${(n / maxHour) * 100}%` }}
                                />
                              </div>
                            ))}
                          </div>
                          <div className="flex justify-between text-[10px] text-base-content/40 mt-1">
                            <span>12a</span>
                            <span>6a</span>
                            <span>12p</span>
                            <span>6p</span>
                            <span>11p</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* Call list */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-base-content/60">
                        {dayStats.total} call{dayStats.total !== 1 ? "s" : ""}
                      </p>
                      <div className="flex gap-1 text-xs">
                        <button
                          onClick={() => setDaySort("time")}
                          className={`px-2 py-1 rounded ${daySort === "time" ? "bg-gray-900 text-white" : "bg-gray-100 dark:bg-base-200 text-gray-600 dark:text-base-content/70"}`}
                        >
                          Time
                        </button>
                        <button
                          onClick={() => setDaySort("duration")}
                          className={`px-2 py-1 rounded ${daySort === "duration" ? "bg-gray-900 text-white" : "bg-gray-100 dark:bg-base-200 text-gray-600 dark:text-base-content/70"}`}
                        >
                          Duration
                        </button>
                      </div>
                    </div>
                    <div className="divide-y divide-base-200">
                      {sortedDayCalls.map((c) => {
                        const t = transcripts[c.id];
                        const expanded = expandedCallId === c.id;
                        return (
                          <div key={c.id}>
                            <div
                              onClick={() => toggleCall(c.id)}
                              className="py-3 flex items-center gap-2 cursor-pointer hover:bg-base-200 -mx-2 px-2 rounded"
                            >
                              <ChevronDownIcon
                                className={`h-4 w-4 text-base-content/40 shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`}
                              />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-base-content">{formatTime(c.startedAt)}</span>
                                  <span
                                    className={`text-[11px] px-1.5 py-0.5 rounded-full capitalize ${
                                      c.connected ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400" : "bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400"
                                    }`}
                                  >
                                    {c.connected ? "Connected" : c.missedReason ? c.missedReason.replace(/_/g, " ") : "No answer"}
                                  </span>
                                </div>
                                <div className="text-sm text-gray-700 dark:text-base-content/80 truncate">
                                  {c.contact || c.number || "Unknown number"}
                                </div>
                                <div className="text-xs text-base-content/40 truncate">
                                  {c.rep}
                                  {c.contact && c.number ? ` · ${c.number}` : ""}
                                </div>
                              </div>
                              <div className="text-right shrink-0">
                                <div className="text-sm font-semibold text-base-content">{formatDuration(c.durationSec)}</div>
                                {c.recording && <div className="text-[11px] text-base-content/40">recording</div>}
                              </div>
                            </div>

                            {expanded && (
                              <div className="pb-3 pl-6 space-y-3">
                                {c.recording ? (
                                  <audio controls preload="none" src={c.recording} className="w-full h-9">
                                    Your browser does not support audio playback.
                                  </audio>
                                ) : (
                                  <p className="text-xs text-base-content/40">No recording for this call.</p>
                                )}
                                {!t || t.status === "loading" ? (
                                  <div className="flex items-center gap-2 text-xs text-base-content/40 py-3">
                                    <Spinner className="h-4 w-4" /> Loading transcript…
                                  </div>
                                ) : t.status === "unavailable" ? (
                                  <p className="text-xs text-base-content/40 py-2">
                                    No transcript available for this call (requires Aircall AI and a recorded call).
                                  </p>
                                ) : t.status === "error" ? (
                                  <p className="text-xs text-rose-500 py-2">Couldn't load transcript. Try again.</p>
                                ) : t.utterances && t.utterances.length > 0 ? (
                                  <div className="space-y-2 bg-base-200 rounded-lg p-3 max-h-72 overflow-y-auto">
                                    {t.utterances.map((u, i) => (
                                      <div
                                        key={i}
                                        className={`flex ${u.speaker === "internal" ? "justify-end" : "justify-start"}`}
                                      >
                                        <div
                                          className={`max-w-[85%] rounded-lg px-3 py-1.5 text-sm ${
                                            u.speaker === "internal"
                                              ? "bg-blue-100 text-blue-900 dark:bg-blue-900/60 dark:text-blue-300"
                                              : "bg-base-100 border border-base-300 text-gray-700 dark:text-base-content/80"
                                          }`}
                                        >
                                          <span className="block text-[10px] uppercase tracking-wide opacity-60 mb-0.5">
                                            {u.speaker === "internal" ? c.rep : c.contact || "Caller"}
                                          </span>
                                          {u.text}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-xs text-base-content/40 py-2">Transcript is empty.</p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SalesCallActivity;
